// The debrief room, part 1 (lane B7 S14; CR-024 the room and its sealed reveal, CR-070 the required roll, CR-071 the
// flags): the status rail and *Debrief readiness*, the attendance roll, the reveal grid, the debrief preparation slot,
// and, per candidate tab, the panels of S15 (alignment, outcome, additional-interview exception, alternates; the
// revise, waive and correction dialogs on each card). Three renders: a participant sees the room; the Coordinator
// "schedules but has no vote" and sees the rail, the gate and the roll, never a score, a flag or a note; the TA Manager
// reads under *Oversight*, sealed until the reveal and read-only after it. The Admin (his TA Admin and HR Systems Admin
// folded into it, DC-228) reads the room as a participant does and records *for* an author ("Record for / correction").

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { debriefOpen, debriefRecordAttendance, debriefReveal, debriefSavePreparationNotes, summaryOpen } from "@/api/tools";
import { errorText, isClientRefusal, useRead, useWrite } from "@/api/useTool";
import {
  ACTION_LABELS, Banner, Button, Card, DEBRIEF_ROOM_LABELS, DEBRIEF_STATUS, ErrorStrip, G1_LABELS, PageHeader, Pill,
  Tabs, toneOf, type RollObserver,
} from "@/mc";
import { Alignment, Alternates, CorrectionDialog, ExceptionRequest, OutcomeCard, ReviseDialog, WaiveDialog } from "@/views/b7/decide";
import { Notes, type NoteMode } from "../summary/Notes";
import { ROOM_LABELS, ROOM_RULES } from "./labels";
import { debriefReadiness, Readiness, roomStatusWord } from "./Readiness";
import { cardsFromRoster, cardsFromSnapshot, RevealGrid, withConcernFlags, type GridCard } from "./RevealGrid";
import { Roll, type AttendanceMark } from "./Roll";
import {
  attendeesOf, isRevealed, roomOf, roomStatusOf, viewerOf, type RoomPanelProps, type RoomView,
} from "./types";

export function roomHref(rid: string, cid: string, applicationId?: string): string {
  return `#requisitions/${rid}/debriefs/${cid}/room${applicationId ? `/${applicationId}` : ""}`;
}

export interface RoomPageProps {
  rid: string;
  cid: string;
  /** The candidate tab (an application id) from the route. */
  tab?: string | undefined;
}

export function RoomPage({ rid, cid, tab }: RoomPageProps) {
  const qc = useQueryClient();
  const openKey = ["debrief.open", rid, cid] as const;
  const readinessKey = ["scheduling.debrief_readiness", rid, cid] as const;
  const read = useRead(openKey, () => debriefOpen({ requisition_id: rid, cycle_id: cid }));
  const readiness = useRead(readinessKey, () => debriefReadiness({ requisition_id: rid, cycle_id: cid }));
  const view = read.data as RoomView | undefined;
  const members = view?.members ?? [];
  // An unknown tab never resolves to another candidate: it opens no candidate at all.
  const applicationId = tab ? (members.some((m) => m.application_id === tab) ? tab : "") : members[0]?.application_id ?? "";
  const viewer = viewerOf(view?.projection);
  const revealed = view ? isRevealed(view) : false;
  const readsSheet = !!applicationId && viewer !== "coordinator" && (viewer !== "oversight" || revealed);
  const summary = useRead(
    ["summary.open", rid, applicationId, cid],
    () => summaryOpen({ requisition_id: rid, application_id: applicationId, cycle_id: cid }),
    { enabled: readsSheet },
  );
  const invalidate = [openKey, readinessKey, ["summary.open", rid]] as const;
  const onChanged = () => { void qc.invalidateQueries({ predicate: (q) => invalidate.some((k) => k.every((part, i) => q.queryKey[i] === part)) }); };
  const attendance = useWrite(
    (marks: AttendanceMark[], key: string) => debriefRecordAttendance({ requisition_id: rid, cycle_id: cid, attendance: marks }, key),
    invalidate,
  );
  const reveal = useWrite((_: null, key: string) => debriefReveal({ requisition_id: rid, cycle_id: cid }, key), invalidate);
  const prep = useWrite(
    // Edit and Correct each append an active version; the note keeps citing what the last version cited (CR-089)
    (input: { body: string; action: NoteMode }, key: string) => {
      const last = roomOf(view!).preparation_note ?? null;
      return debriefSavePreparationNotes({
        requisition_id: rid, cycle_id: cid, body: input.body, state: "active",
        source_refs: last?.source_refs ?? [], expected_version: last?.version ?? null,
      }, key);
    },
    invalidate,
  );
  const [writeError, setWriteError] = useState<string | null>(null);
  const run = <T,>(attempt: Promise<T>) => { setWriteError(null); attempt.catch((e: unknown) => setWriteError(errorText(e))); };

  if (!view) {
    return read.error
      ? <ErrorStrip message={errorText(read.error)} onRetry={isClientRefusal(read.error) ? undefined : () => void read.refetch()} />
      : <Card title={ROOM_LABELS.debriefRoom} loading />;
  }

  const status = roomStatusOf(view);
  const word = roomStatusWord(status);
  const room = roomOf(view);
  const attendees = attendeesOf(view);
  // the cycle read names no observer yet: the roll draws the required attendees alone
  const observers: RollObserver[] = [];
  const panel: RoomPanelProps = { view, applicationId, onChanged };
  const sheet = summary.data;
  const shown = view.members.find((m) => m.application_id === applicationId);
  const cards: GridCard[] = withConcernFlags(
    sheet?.snapshot ? cardsFromSnapshot(sheet.snapshot) : cardsFromRoster(attendees, readiness.data),
    shown?.concern_flags,
  );
  const rollEditable = viewer === "participant" && view.can.record_attendance === true && (status === "scheduled" || status === "roll_call");
  const firstGap = view.reveal_gaps[0];
  const revealButton = !revealed && viewer === "participant" ? (
    <Button
      tone="primary" sm  can={view.can.reveal === true || view.can.reveal_blocked === true} busy={reveal.isPending}
      blocked={firstGap ? { reason: firstGap } : undefined}
      onClick={() => run(reveal.mutateAsync(null))}
    >
      {ACTION_LABELS.reveal}
    </Button>
  ) : null;
  const cardActions = viewer === "participant" ? (card: GridCard) => {
    const scorecardId = card.scorecardId ?? undefined;
    if (card.status !== "submitted") return <WaiveDialog {...panel} scorecardId={scorecardId} />;
    return revealed ? <><ReviseDialog {...panel} scorecardId={scorecardId} /><CorrectionDialog {...panel} scorecardId={scorecardId} /></> : null;
  } : undefined;

  return (
    <div className="mc-stack">
      <PageHeader
        title={ROOM_LABELS.debriefRoom}
        pills={<Pill tone={toneOf(word)}>{word}</Pill>}
        meta={[view.requisition_id, view.requisition_title]}
      />
      {viewer === "coordinator" ? <Banner tone="info">{ROOM_RULES.coordinatorNoVote}</Banner> : null}
      {viewer === "oversight" ? <Banner tone="neutral">{DEBRIEF_ROOM_LABELS.oversight}</Banner> : null}
      {writeError ? <ErrorStrip message={writeError} /> : null}
      <Readiness
        status={status}
        readiness={readiness.data}
        loading={readiness.isLoading}
        refused={!!readiness.error}
        blockedReasons={view.blocked_reasons ?? []}
      />
      <Roll
        key={attendees.map((a) => `${a.person_id}:${a.status ?? ""}`).join("|")}
        attendees={attendees}
        observers={observers}
        editable={rollEditable}
        status={status === "roll_call" ? DEBRIEF_STATUS.rollCall : undefined}
        busy={attendance.isPending}
        onSave={(marks) => run(attendance.mutateAsync(marks))}
      />
      {viewer === "coordinator" ? null : (
        <>
          {members.length > 1 ? (
            <Tabs
              label={ROOM_LABELS.debriefRoom}
              route
              active={applicationId}
              items={members.map((m) => ({ key: m.application_id, label: m.candidate, href: roomHref(rid, cid, m.application_id) }))}
            />
          ) : null}
          <RevealGrid
            cards={cards}
            sealed={!revealed}
            action={revealButton}
            cardActions={cardActions}
            loading={readsSheet && summary.isLoading}
          />
          <Notes
            title={G1_LABELS.debriefPreparation}
            note={room.preparation_note ?? null}
            canWrite={viewer === "participant" && view.can.save_preparation_notes === true}
            busy={prep.isPending}
            onSave={(body, action) => run(prep.mutateAsync({ body, action }))}
            saveHs="room.prep.save"
            
          />
          {applicationId ? (
            <>
              <Alignment {...panel} />
              <OutcomeCard {...panel} />
              <ExceptionRequest {...panel} />
              <Alternates {...panel} />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
